import clsx from 'clsx';
import * as React from 'react';

import {
  createStyles,
  ListItem,
  Theme,
  withStyles,
  WithStyles,
} from '@nelnet/unifi-components-react';

import Link from '../../../modules/Link';

import SubNavHeadingItems from './SubNavHeadingItems';

const styles = ({ palette, typography, spacing }: Theme) =>
  createStyles({
    item: {
      display: 'block',
      paddingTop: 0,
      paddingBottom: 0,
    },
    itemLeaf: {
      display: 'block',
      paddingTop: 0,
      paddingBottom: 0,
    },
    link: {
      padding: spacing(1),
      display: 'block',
      fontSize: '.9rem',
      '&:hover, &:focus': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
    },
    linkLeaf: {
      letterSpacing: 0,
      justifyContent: 'flex-start',
      textTransform: 'none',
      width: '100%',
      fontWeight: typography.fontWeightRegular,
      '&.depth-0': {
        lineHeight: typography.button.lineHeight,
      },
    },
    collapseUL: {
      margin: 0,
      padding: 0,
    },
    active: {
      backgroundColor: 'transparent !important',
      '& > a, & > a': {
        fontWeight: 600,
      },
      '&:not(.depth-0) > a': {
        color: palette.primary.main,
      },
    },
  });

interface INavDrawerItemProps extends WithStyles<typeof styles> {
  depth: number;
  href?: string;
  showChildren?: boolean;
  title: string;
  active: boolean;
  item?: string;
}

const NavDrawerItem: React.FunctionComponent<INavDrawerItemProps> = ({
  children,
  classes,
  depth,
  href,
  showChildren,
  title,
  active,
  item,
  ...rest
}) => {
  const [open] = React.useState(showChildren || false);

  React.useEffect(() => {
    // Center the selected item in the list container.
    const activeElement = document.querySelector(`.${classes.active}`);
    if (activeElement && activeElement.scrollIntoView) {
      activeElement.scrollIntoView({});
    }
  }, [showChildren]);

  const style = {
    paddingLeft: 8 * (3 + 2 * depth),
  };

  if (href) {
    const showSubNav = active && depth === 2 && !href.includes('changelog');
    return (
      <ListItem
        className={classes.itemLeaf}
        selected={active}
        classes={{
          selected: classes.active,
        }}
        disableGutters
        {...rest}
      >
        <Link
          className={clsx(classes.link, classes.linkLeaf, `depth-${depth}`)}
          style={style}
          color="inherit"
          underline="none"
          href={href}
        >
          {title}
        </Link>
        {showSubNav && <SubNavHeadingItems parentSlug={href} parentDepth={2} />}
      </ListItem>
    );
  }

  let indexLink = item || '';
  indexLink =
    !indexLink.includes('http') && !indexLink.startsWith('/') ? `/${indexLink}` : indexLink;

  const includeSubNav = open && depth === 1 && indexLink.includes('theming');

  return (
    <ListItem
      className={clsx(classes.item, `depth-${depth}`)}
      selected={active}
      classes={{
        selected: classes.active,
      }}
      disableGutters
      {...rest}
    >
      <Link
        className={clsx(classes.link, classes.linkLeaf, `depth-${depth}`)}
        style={style}
        color="inherit"
        underline="none"
        href={indexLink}
      >
        {title}
      </Link>

      {includeSubNav && <SubNavHeadingItems parentSlug={indexLink} parentDepth={1} />}

      {open && <ul className={classes.collapseUL}>{children}</ul>}
    </ListItem>
  );
};

export default withStyles(styles)(NavDrawerItem);
